import React, { useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { COMMUNICATION_TIMELINE_ENTRY } from "src/stores/CommunicationStore";
import { CommunicationTimelineEntry } from "./CommunicationTimelineEntry";
import { Route, selectRoute } from "src/config/routes";
import { useHistory } from "react-router";
import styles from "./InlineCommunicationTimelineEntry.module.css";
import {
    getInlineCommunicationProgressSteps,
    getIsInlineCommunicationConnectorFilled
} from "./InlineCommunicationProgress.utils";

interface IInlineCommunicationTimelineEntryProps {
    assemblyId: string;
    entryState: COMMUNICATION_TIMELINE_ENTRY;
}

const InlineCommunicationTimelineEntryBase = ({ assemblyId, entryState }: IInlineCommunicationTimelineEntryProps) => {
    const { t } = useTranslation();
    const { communicationStore, assemblyStore, authStore } = useContext(RootStoreContext);
    const history = useHistory();
    const openCommunicationLabel = t("screens.assemblies.communicationcontent.inline_progress.open_communication");
    const progressSteps = getInlineCommunicationProgressSteps(assemblyStore.currentAssembly?.state, entryState);

    useEffect(() => {
        if (assemblyId) {
            communicationStore.initAssemblyAndHistory(assemblyId);
            communicationStore.getProtocolOrAgendaitemFile(assemblyId);
        }
    }, [assemblyId, communicationStore]);

    if (!assemblyId) {
        return null;
    }

    if (communicationStore.isCommunicationStoreInitializing) {
        return <UI.RotatingSpinner noLogo size={50} />;
    }

    const entry = communicationStore.communicationEntries.find(
        (communicationEntry) => communicationEntry.state === entryState
    );

    if (!entry) {
        return null;
    }

    const header = (
        <div className={styles.InlineTitleTools}>
            <div className={styles.Progress}>
                {progressSteps.map((step, index) => (
                    <React.Fragment key={step.state}>
                        <div className={`${styles.ProgressStep} ${styles[step.className]}`}>
                            <span>{t(`screens.assemblies.communicationcontent.inline_progress.${step.labelKey}`)}</span>
                        </div>
                        {index < progressSteps.length - 1 ? (
                            <span
                                className={`${styles.ProgressSeparator} ${
                                    getIsInlineCommunicationConnectorFilled(
                                        step.state,
                                        progressSteps[index + 1].state,
                                        entryState,
                                        assemblyStore.currentAssembly?.state
                                    )
                                        ? styles.ProgressSeparatorFilled
                                        : ""
                                }`}
                            />
                        ) : null}
                    </React.Fragment>
                ))}
            </div>
            <button
                type="button"
                className={styles.CommunicationLinkButton}
                title={openCommunicationLabel}
                aria-label={openCommunicationLabel}
                onClick={() =>
                    history.push(
                        selectRoute(Route.assemblyCommunication, authStore.user?.role, {
                            assemblyid: assemblyId
                        })
                    )
                }
            >
                <UI.Icon icon={UI.SVGIcon.ExternalLink} size={16} />
            </button>
        </div>
    );

    return <CommunicationTimelineEntry entry={entry} isInlineCard inlineHeader={header} />;
};

export const InlineCommunicationTimelineEntry = Sentry.withProfiler(observer(InlineCommunicationTimelineEntryBase));
